import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// styles
import { UploadImageStyled } from "./styles.js";
import colors from "core/colors";

// components
import FlagList from "./Flags/FlagList.js";
import PhotoModRefreshingImage from "../PhotoModRefreshingImage/index.js";

// hooks
import { useClearFlags } from "../../hooks";

import { blurUpload } from "../../slices/uploadFooterActionsSlice";

import { moderatorIconKeys } from "../../config.js";

const UploadImage = ({ uploadId, useThumbnail = false }) => {
    const dispatch = useDispatch();

    const footerActions =
        useSelector(state => state.uploadFooterActions.entities[uploadId]) ||
        {};
    const upload = useSelector(state => state.uploads.entities[uploadId]);
    const selectedBatchId = useSelector(state => state.selectedBatch.id);
    const selectedbatch = useSelector(
        state => state.batches.entities[selectedBatchId]
    );

    const onSingleUploadFlagClear = ({ flagText }) => {
        if (flagText === "EXP") {
            return clearExplicitStatus([upload.id]);
        }

        if (flagText === "REL") {
            return clearNeedsReleaseStatus([upload.id]);
        }

        if (flagText === "EUO") {
            return clearEditorialUseOnly([upload.id]);
        }

        if (flagText === "TXT") {
            return clearHasWarningText([upload.id]);
        }
    };

    useEffect(() => {
        const previouslyBlurredUploads =
            JSON.parse(localStorage.getItem("modBlurredUploads")) || [];

        if (previouslyBlurredUploads.includes(id)) {
            dispatch(blurUpload(id));
        }
    }, []);

    useEffect(() => {
        if (upload.autoNudity || upload.autoViolence) {
            dispatch(blurUpload(id));
        }
    }, [upload]);

    const {
        clearExplicitStatus,
        clearNeedsReleaseStatus,
        clearEditorialUseOnly,
        clearHasWarningText
    } = useClearFlags();

    const generateFlags = useCallback(
        upload => {
            const flags = [];
            const {
                hasRecognizablePeopleOrProperty,
                editorialUseOnly,
                autoWarningText,
                autoNudity,
                autoViolence
            } = upload;

            if (hasRecognizablePeopleOrProperty) {
                flags.push({
                    id: 1,
                    text: "REL",
                    bgColor: colors.alternative.lightMode.tang
                });
            }

            if (editorialUseOnly) {
                flags.push({
                    id: 2,
                    text: "EUO",
                    bgColor: colors.alternative.lightMode.tang
                });
            }

            if (autoWarningText) {
                flags.push({
                    id: 3,
                    text: "TXT",
                    bgColor: colors.alternative.lightMode.tang
                });
            }

            if (autoNudity || autoViolence) {
                flags.push({
                    id: 4,
                    text: "EXP",
                    bgColor: colors.functional.darkMode.tandoori
                });
            }

            return flags;
        },
        [upload]
    );

    if (!upload) return null;
    const { id, thumbnail, title, uploadedImageUrl } = upload;

    return (
        <UploadImageStyled>
            <FlagList
                flags={generateFlags(upload)}
                upload={upload}
                onFlagClear={onSingleUploadFlagClear}
                isDisabled={!selectedbatch?.isEditable?.flags}
            />
            <PhotoModRefreshingImage
                id={uploadId}
                src={useThumbnail ? thumbnail : uploadedImageUrl}
                alt={`File name ${title}`}
                imageProps={{
                    imageIsBlurred: footerActions[moderatorIconKeys.BLUR],
                    "data-testid": `upload-image-testId-${id}`
                }}
            />
        </UploadImageStyled>
    );
};

export default UploadImage;

// prop-types
UploadImage.propTypes = {
    uploadId: PropTypes.string.isRequired,
    useThumbnail: PropTypes.bool
};
